/**
 * Widget C: Contribution Rate Distribution
 * 
 * Displays a vertical bar chart showing the distribution of clients across
 * contribution rate bands: Under 3%, 3-5%, 5-10%, 10-15%, Over 15%.
 */

import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateContributionRateDistribution, ContributionRateData } from "../utils/widgetCalculations";
import { UNIFIED_CHART_COLORS, SEMANTIC_PASTELS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";

interface ContributionRateWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const BAND_LABELS = {
  under3: "Under 3%",
  "3to5": "3-5%",
  "5to10": "5-10%",
  "10to15": "10-15%",
  over15: "Over 15%",
};

// Use unified colors in a sequential gradient from low to high contribution rates
const BAND_COLORS = {
  under3: UNIFIED_CHART_COLORS[4],    // Coral (low contribution)
  "3to5": UNIFIED_CHART_COLORS[8],    // Apricot (low-medium)
  "5to10": UNIFIED_CHART_COLORS[5],   // Lemon yellow (medium)
  "10to15": UNIFIED_CHART_COLORS[0],  // Teal (medium-high)
  over15: UNIFIED_CHART_COLORS[6],    // Mint green (high contribution)
};

export function ContributionRateWidget({
  clients,
  isLoading = false,
  userId,
}: ContributionRateWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading } = useQuery({
    queryKey: ["contribution-rate", userId],
    queryFn: async () => {
      const result = await api.getContributionRate(userId);
      console.log("🔍 ContributionRateWidget: API response:", result);
      console.log("🔍 ContributionRateWidget: Has bands?", !!result?.bands);
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const data = useMemo(() => {
    // Only use API data - no sample data fallback
    let result: {
      bands: {
        under3: number;
        "3to5": number;
        "5to10": number;
        "10to15": number;
        over15: number;
      };
    };
    
    console.log("🔍 ContributionRateWidget: Processing data - apiData:", apiData);
    
    if (apiData && apiData.bands) {
      // API data is available and valid - use it
      console.log("✅ ContributionRateWidget: Using API data");
      result = apiData;
    } else {
      // No API data available - return empty structure
      console.log("❌ ContributionRateWidget: No API data, returning empty structure");
      result = {
        bands: {
          under3: 0,
          "3to5": 0,
          "5to10": 0,
          "10to15": 0,
          over15: 0,
        },
      };
    }
    
    // Transform to chart data format
    const chartData = Object.entries(result.bands).map(([key, count]) => ({
      band: BAND_LABELS[key as keyof typeof BAND_LABELS],
      count,
      fill: BAND_COLORS[key as keyof typeof BAND_COLORS],
      key,
    }));

    const totalClients = chartData.reduce((sum, item) => sum + item.count, 0);

    return { chartData, totalClients };
  }, [apiData]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No contribution rate data available for the selected clients.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  // Add totalClients to each data point for tooltip
  const chartDataWithTotal = data.chartData.map(item => ({
    ...item,
    totalClients: data.totalClients,
  }));

  return (
    <Card>
      <CardContent>
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartDataWithTotal} 
              margin={{
                top: 10,
                right: 20,
                left: 25,
                bottom: -60, // Minimal bottom margin to bring chart as close to bottom as possible
              }}
            >
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="band" 
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const percentage = data.totalClients > 0
                      ? ((value / data.totalClients) * 100).toFixed(1)
                      : "0.0";
                    return [`${value} clients (${percentage}%)`, name];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {chartDataWithTotal.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => {
            const percentage = data.totalClients > 0
              ? ((item.count / data.totalClients) * 100).toFixed(1)
              : "0.0";
            return (
              <div key={item.key} className="space-y-1">
                <div className="text-lg font-bold" style={{ color: item.fill }}>
                  {item.count}
                </div>
                <div className="text-xs font-medium text-foreground">{item.band}</div>
                <div className="text-xs text-muted-foreground">{percentage}%</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

